import { Switch } from "@headlessui/react";
import { type ReactNode, forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { Button, type ButtonProps } from "./Button";

import type { PolymorphicRef } from "types";

type ToggleSwitchProps<T extends React.ElementType> = ButtonProps<T> & {
  labelStart?: ReactNode;
  labelEnd?: ReactNode;
  switchClassName?: string;
};

export const ToggleSwitch = forwardRef(function ToggleSwitch<
  T extends React.ElementType = "button",
>(
  {
    className,
    switchClassName,
    isSelected,
    disabled,
    labelStart,
    labelEnd,
    ...rest
  }: ToggleSwitchProps<T>,
  ref: PolymorphicRef<T>,
) {
  const switchElement = (
    <Switch
      as="div"
      checked={isSelected}
      className={twMerge(
        "group flex h-3.5 w-7 min-w-7 cursor-pointer items-center rounded-full bg-black-tint-70 transition data-[checked]:bg-blue data-[checked]:data-[disabled]:bg-black-tint-70",
        switchClassName,
      )}
      disabled={disabled}
    >
      <span className="size-3 translate-x-[0.0675rem] rounded-full bg-white shadow-md transition group-data-[checked]:translate-x-[0.9375rem]" />
    </Switch>
  );

  return (
    // @ts-expect-error TypeScript doesn't understand that `Button` is polymorphic
    <Button
      className={twMerge("flex items-center gap-2 transition", className)}
      disabled={disabled}
      eventProps={{ isEnabled: !isSelected }}
      isSelected={isSelected}
      ref={ref}
      {...rest}
    >
      {labelStart}
      {switchElement}
      {labelEnd}
    </Button>
  );
}) as <T extends React.ElementType = "button">(
  props: ToggleSwitchProps<T> & { ref?: PolymorphicRef<T> },
) => React.ReactNode;
